import { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useWatch } from 'react-hook-form';
import { useQueries, useQuery } from '@tanstack/react-query';

import { fetchBomDetail } from '#/api/momMgmt/settings/bom/fetchDetail';
import { fetchMtrDetail } from '#/api/imsMgmt/settings/mtr/fetchDetail';
import CoverOverlay from '#/modules/commonComponents/CoverOverlay';
import FloatingSelect from '#/modules/commonComponents/form/select';
import { parseDetailAssemblyOptions } from '#/modules/imsMgmt/settings/mtr/helper/assemblyOptionsSerialize';
import { cn } from '#/lib/utils';
import { getBomGroupMaterials } from '#/modules/momMgmt/settings/bom/helper/bomGroupsForm';
import { compareMtrNo } from '#/modules/momMgmt/workOrder/helper/compareMtrNo';
import { bomDetailQueryKey, detailQueryKey } from '#/utils/queryKeys';

function resolveBomMaterialId(material) {
  return material?.mtrId
    ?? material?.sourceMtrId
    ?? material?.mtr?.id
    ?? '';
}

function collectBomMaterialIds(bom) {
  const ids = [];
  (bom?.groups ?? []).forEach((group) => {
    getBomGroupMaterials(group).forEach((material) => {
      const mtrId = resolveBomMaterialId(material);
      if (mtrId) {
        ids.push(mtrId);
      }
    });
  });
  return [...new Set(ids)];
}

function buildGroupRows(bom, mtrById) {
  const rows = [];
  (bom?.groups ?? []).forEach((group) => {
    getBomGroupMaterials(group).forEach((material) => {
      const mtrId = resolveBomMaterialId(material);
      if (!mtrId) {
        return;
      }
      const mtrDetail = mtrById.get(mtrId);
      const kind = mtrDetail?.mtrKind
        ?? material?.mtr?.mtrKind
        ?? material?.mtr?.materialKind;
      if (kind !== 'GROUP') {
        return;
      }
      const assemblyOptions = [...parseDetailAssemblyOptions(mtrDetail)]
        .sort((left, right) => compareMtrNo(left.mtrNo, right.mtrNo));
      rows.push({
        sourceGroupMaterialId: mtrId,
        sourceGroupMtrNo: mtrDetail?.mtrNo ?? material?.mtr?.mtrNo ?? '',
        sourceGroupMtrName: mtrDetail?.name ?? material?.mtr?.name ?? '',
        groupName: group.name ?? '',
        options: assemblyOptions.map((option) => ({
          value: option.optionMtrId,
          label: [option.mtrNo, option.name].filter(Boolean).join(' ')
            || option.optionMtrId,
        })),
      });
    });
  });
  return rows.sort((left, right) => compareMtrNo(
    left.sourceGroupMtrNo,
    right.sourceGroupMtrNo,
  ));
}

function formSelectionsGroupKey(selections) {
  return (selections ?? [])
    .map((item) => item?.sourceGroupMaterialId)
    .filter(Boolean)
    .join('|');
}

function BomSelectionsShell({ children }) {
  return (
    <section
      className={cn(
        'form-section @container/form-section mt-4 min-w-0',
        'border-t border-dashed border-slate-400 pt-4',
      )}
    >
      <h3 className="form-subsection__title">替代料選擇</h3>
      <div
        className={cn(
          'form-section__body create-edit-panel-scroll relative min-h-16',
        )}
      >
        {children}
      </div>
    </section>
  );
}

BomSelectionsShell.propTypes = {
  children: PropTypes.node,
};

/**
 * GROUP → NORMAL 替代料選擇；依 bomId 拉 BOM＋各料 detail。
 * 嵌在「成品」區塊下方（subtitle＋虛線頂邊）。
 */
const WorkOrderBomSelectionsEditor = ({
  control,
  errors,
  setValue,
  getValues,
  bomId,
  disabled = false,
}) => {
  const formSelections = useWatch({
    control,
    name: 'bomMaterialSelections',
    defaultValue: [],
  });

  const bomQuery = useQuery({
    queryKey: bomDetailQueryKey(bomId),
    queryFn: () => fetchBomDetail(bomId),
    enabled: Boolean(bomId),
  });

  const bom = bomQuery.data?.data;
  const materialIds = useMemo(() => collectBomMaterialIds(bom), [bom]);

  const mtrQueries = useQueries({
    queries: materialIds.map((mtrId) => ({
      queryKey: detailQueryKey('mtr', mtrId),
      queryFn: () => fetchMtrDetail(mtrId),
      enabled: Boolean(mtrId),
    })),
  });

  const mtrById = useMemo(() => {
    const map = new Map();
    mtrQueries.forEach((query, index) => {
      const id = materialIds[index];
      const data = query.data?.data;
      if (id && data) {
        map.set(id, data);
      }
    });
    return map;
  }, [mtrQueries, materialIds]);

  // bomId 有值但 BOM 未 success 時不可 ready（避免 materialIds=[] 誤判）
  const mtrsReady = bomQuery.isSuccess
    && (
      materialIds.length === 0
      || mtrQueries.every(
        (query) => !query.isLoading && (query.isSuccess || query.isError),
      )
    );

  const groupRows = useMemo(() => {
    if (!bom || !mtrsReady) {
      return [];
    }
    return buildGroupRows(bom, mtrById);
  }, [bom, mtrById, mtrsReady]);

  const groupKey = groupRows.map((row) => row.sourceGroupMaterialId).join('|');
  const formGroupKey = formSelectionsGroupKey(formSelections);

  const isHydrating = Boolean(bomId)
    && !bomQuery.isError
    && (
      bomQuery.isPending
      || bomQuery.isLoading
      || !mtrsReady
      || formGroupKey !== groupKey
    );

  useEffect(() => {
    if (!bomId) {
      setValue('bomMaterialSelections', []);
      return;
    }
    if (!mtrsReady) {
      return;
    }

    const current = getValues('bomMaterialSelections') ?? [];
    const next = groupRows.map((row) => {
      const existing = current.find(
        (item) => item.sourceGroupMaterialId === row.sourceGroupMaterialId,
      );
      return {
        sourceGroupMaterialId: row.sourceGroupMaterialId,
        selectedMaterialId: existing?.selectedMaterialId ?? '',
        sourceGroupMtrNo: row.sourceGroupMtrNo,
        sourceGroupMtrName: row.sourceGroupMtrName,
      };
    });

    const sameLength = next.length === current.length;
    const sameIds = sameLength && next.every(
      (row, index) => row.sourceGroupMaterialId === current[index]?.sourceGroupMaterialId
        && row.selectedMaterialId === current[index]?.selectedMaterialId,
    );
    if (sameIds) {
      return;
    }

    setValue('bomMaterialSelections', next, { shouldValidate: false });
  }, [bomId, groupKey, mtrsReady, groupRows, getValues, setValue]);

  if (!bomId) {
    return (
      <BomSelectionsShell>
        <p className="text-sm text-muted-foreground">請先選擇成品。</p>
      </BomSelectionsShell>
    );
  }

  return (
    <BomSelectionsShell>
      <CoverOverlay show={isHydrating} isLoading={isHydrating} />
      {isHydrating ? (
        <p className="text-sm text-muted-foreground">載入替代料…</p>
      ) : null}
      {bomQuery.isError ? (
        <p className="text-sm text-destructive">載入 BOM 替代料失敗。</p>
      ) : null}
      {!isHydrating && mtrsReady && groupRows.length === 0 ? (
        <p className="text-sm text-muted-foreground">此 BOM 無需選擇替代料。</p>
      ) : null}
      {groupRows.length > 0 ? (
        <div className={cn('grid grid-cols-1 gap-2', '@2xl/sheet:grid-cols-2')}>
          {groupRows.map((row, index) => (
            <div
              key={row.sourceGroupMaterialId}
              className="min-w-0 space-y-2 rounded-md border border-slate-200 bg-slate-50 px-2 py-2"
            >
              <div className="min-w-0">
                {row.groupName ? (
                  <p className="mb-0.5 text-xs text-slate-500">{row.groupName}</p>
                ) : null}
                <p className="truncate text-sm font-medium text-gray-800">
                  {row.sourceGroupMtrName || '（未命名）'}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {row.sourceGroupMtrNo || row.sourceGroupMaterialId || '—'}
                </p>
              </div>
              <FloatingSelect
                id={`bomMaterialSelections.${index}.selectedMaterialId`}
                label="選用一般物料"
                options={row.options}
                control={control}
                errors={errors}
                isDisabled={disabled || row.options.length === 0}
                noOptionsMessage="此替代料無可用子料"
              />
            </div>
          ))}
        </div>
      ) : null}
    </BomSelectionsShell>
  );
};

WorkOrderBomSelectionsEditor.propTypes = {
  control: PropTypes.object.isRequired,
  errors: PropTypes.object,
  setValue: PropTypes.func.isRequired,
  getValues: PropTypes.func.isRequired,
  bomId: PropTypes.string,
  disabled: PropTypes.bool,
};

export default WorkOrderBomSelectionsEditor;
